// BuggySection.jsx
// ─ Lovable, cheerful, Japanese-family-friendly redesign
// ─ Bold chunky cards, warm earthy palette, playful details
// ─ Pure Tailwind, bilingual ja/en

import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

/* ─────────────────────────────────────────────────────────
   DATA
───────────────────────────────────────────────────────── */
const PRICES = [
  {
    titleJa: '通常コース',
    titleEn: 'Normal Course',
    subJa: 'NORMAL COURSE',
    subEn: 'ノーマルコース',
    timeJa: '10分',
    timeEn: '10 min',
    price: '¥1,500',
    perJa: '/ 1人',
    perEn: '/ per person',
    icon: '🏁',
    featured: false,
  },
  {
    titleJa: 'おすすめコース',
    titleEn: 'Recommended',
    subJa: 'RECOMMENDED',
    subEn: 'おすすめ',
    timeJa: '25分',
    timeEn: '25 min',
    price: '¥3,500',
    perJa: '/ 1人',
    perEn: '/ per person',
    icon: '⭐',
    featured: true,
    ribbon: true,
  },
  {
    titleJa: '初回講習料',
    titleEn: 'First Lesson Fee',
    subJa: 'FIRST LESSON FEE',
    subEn: '初回講習料',
    timeJa: '',
    timeEn: '',
    price: '¥500',
    perJa: '初回のみ',
    perEn: 'First time only',
    icon: '🎓',
    featured: false,
  },
];

const GALLERY = [
  { src: '/images/buggyteam2.jpeg',  fallback: '🏎️' },
  { src: '/images/buggyteam.jpg', fallback: '🌲' },
  { src: '/images/buggy.JPG', fallback: '🏞️' },
  { src: '/images/buggy.png', fallback: '🎉' },
];

const GALLERY_GRADIENTS = [
  'from-[#1a3d20] to-[#2d6b38]',
  'from-[#3d2010] to-[#6b3a1f]',
  'from-[#1a2a3d] to-[#2d4a6b]',
  'from-[#3d1a1a] to-[#6b2d2d]',
];

/* ─────────────────────────────────────────────────────────
   SUB-COMPONENTS
───────────────────────────────────────────────────────── */

/** Section label with decorative lines */
function SectionLabel({ text }) {
  return (
    <div className="flex items-center justify-center gap-4 mb-5">
      <div className="flex-1 max-w-[80px] h-[3px] rounded-full bg-gradient-to-r from-transparent to-[#c8340a]" />
      <span className="text-[11px] font-extrabold tracking-[0.35em] uppercase text-[#c8340a]">
        {text}
      </span>
      <div className="flex-1 max-w-[80px] h-[3px] rounded-full bg-gradient-to-l from-transparent to-[#c8340a]" />
    </div>
  );
}

/** Price card */
function PriceCard({ item, isJa }) {
  return (
    <motion.div
      whileHover={{ y: -6 }}
      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
      className={[
        'rounded-[24px] overflow-hidden relative',
        'bg-[#fffdf8]',
        item.featured
          ? 'border-[2.5px] border-[#d89c10] shadow-[0_6px_0_#a87810,0_10px_28px_rgba(216,156,16,.2)]'
          : 'border-[2.5px] border-[#d8c6a8] shadow-[0_6px_0_#d8c6a8,0_10px_24px_rgba(30,40,20,.07)]',
      ].join(' ')}
    >
      {/* recommended ribbon */}
      {item.ribbon && (
        <div className="
          absolute top-3.5 right-0 z-10
          bg-[#c8340a] text-white
          text-[10px] font-extrabold tracking-[.12em]
          px-4 py-1.5
          rounded-l-md
          shadow-[2px_2px_0_#8a2308]
          before:content-[''] before:absolute before:right-0 before:-bottom-1.5
          before:border-solid before:border-[6px_8px_0_0]
          before:border-[#8a2308_transparent_transparent_transparent]
        ">
          おすすめ
        </div>
      )}

      {/* card top */}
      <div className={[
        'pt-6 pb-4 px-4 text-center border-b-[2px]',
        item.featured
          ? 'bg-[#d89c10] border-[#a87810]'
          : 'bg-[#f4f9ee] border-[#e7dbc7]',
      ].join(' ')}>
        <div className="text-[32px] mb-1.5">{item.icon}</div>
        <h3 className={[
          'text-[18px] font-extrabold leading-tight',
          item.featured ? 'text-white' : 'text-[#173617]',
        ].join(' ')}>
          {isJa ? item.titleJa : item.titleEn}
        </h3>
        <p className={[
          'text-[10px] font-bold tracking-[.15em] mt-1',
          item.featured ? 'text-white/65' : 'text-[#aaa]',
        ].join(' ')}>
          {isJa ? item.subJa : item.subEn}
        </p>
      </div>

      {/* card body */}
      <div className="px-5 py-6 text-center">
        {(isJa ? item.timeJa : item.timeEn) && (
          <p className="text-[32px] font-extrabold text-[#173617] mb-2 leading-none">
            {isJa ? item.timeJa : item.timeEn}
          </p>
        )}
        <p className={[
          'font-extrabold leading-none',
          'text-[clamp(44px,5vw,56px)]',
          item.featured ? 'text-[#d89c10]' : 'text-[#173617]',
        ].join(' ')}>
          {item.price}
        </p>
        <p className="text-[13px] font-bold text-[#aaa] mt-2">
          {isJa ? item.perJa : item.perEn}
        </p>
      </div>
    </motion.div>
  );
}

/* ─────────────────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────────────────── */
export default function BuggySection() {
  const { i18n } = useTranslation();
  const isJa = i18n.language?.startsWith('ja');

  return (
    <section
      id="buggy-section"
      className="bg-[#f5f0e6] py-16 px-4 overflow-hidden relative"
    >
      {/* dot pattern */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle,rgba(100,60,20,.045) 1px,transparent 1px)',
          backgroundSize: '24px 24px',
        }}
      />

      <div className="max-w-7xl mx-auto relative z-10">

        {/* ── SECTION LABEL ── */}
        <SectionLabel text="Activity No.1 · バギー体験" />

        {/* ══ HERO BLOCK ══ */}
        <div className="
          grid lg:grid-cols-2 rounded-[28px] overflow-hidden mb-4
          border-[2.5px] border-[#e7dbc7]
          bg-white
          shadow-[0_16px_48px_rgba(30,60,20,.1),0_2px_0_#d89c10]
          relative
        ">
          {/* rainbow top border */}
          <div className="absolute top-0 left-0 right-0 h-[6px] z-10"
            style={{ background:'repeating-linear-gradient(90deg,#c8340a 0,#c8340a 40px,#d89c10 40px,#d89c10 80px,#2d6b38 80px,#2d6b38 120px)' }}
          />

          {/* LEFT */}
          <div className="p-10 md:p-14 flex flex-col justify-center relative overflow-hidden">
            {/* faded big number */}
            <div className="absolute right-0 bottom-0 text-[220px] font-extrabold leading-none text-[#c8340a]/[0.05] select-none pointer-events-none"
              style={{ fontFamily:'Syne,sans-serif' }}>1</div>

            {/* No.1 badge — ribbon style */}
            <div className="
              inline-flex items-center gap-2
              bg-[#c8340a] text-white
              rounded-[10px_999px_999px_10px]
              px-6 py-3 mb-7 w-fit
              text-[14px] font-extrabold tracking-wide
              shadow-[4px_4px_0_#8a2308]
              relative z-10
            ">
              <span className="text-[20px]">👑</span>
              {isJa ? 'No.1 人気体験' : 'No.1 Popular Activity'}
            </div>

            {/* title */}
            <h2 className="relative z-10 font-extrabold text-[#173617] leading-[.9] tracking-tight mb-5"
              style={{ fontSize: 'clamp(52px,7vw,80px)' }}>
              {isJa ? (
                <>バギー<span className="text-[#c8340a] block">体験</span></>
              ) : (
                <>BUGGY<span className="text-[#c8340a] block">EXPERIENCE</span></>
              )}
            </h2>

            {/* description */}
            <p className="relative z-10 text-[16px] font-bold text-[#444] leading-[1.85] mb-6">
              {isJa ? (
                <>
                  緑に囲まれた本格オフロードを、<br />
                  バギーで思いきり駆け抜けよう！
                  <span className="block text-[13px] text-[#888] mt-1 font-bold">
                    Enjoy a real off-road adventure in nature!
                  </span>
                </>
              ) : (
                <>
                  Enjoy a real off-road adventure<br />
                  surrounded by beautiful nature!
                  <span className="block text-[13px] text-[#888] mt-1 font-bold">
                    緑に囲まれた本格オフロード体験！
                  </span>
                </>
              )}
            </p>

            {/* age badge */}
            <div className="
              inline-flex items-center gap-2
              bg-[#edf7ed] border-[2px] border-[#2d6b38]
              rounded-full px-6 py-3 w-fit
              text-[14px] font-extrabold text-[#1f4d2b]
              relative z-10
            ">
              👶 {isJa ? '対象年齢：10歳〜（小学校高学年）' : 'Recommended Age: 10+'}
            </div>

            {/* decorative leaves */}
            <div className="absolute left-2 top-1/2 opacity-15 text-6xl select-none pointer-events-none">🍃</div>
            <div className="absolute right-6 bottom-6 opacity-15 text-5xl select-none pointer-events-none">🌿</div>
          </div>

          {/* RIGHT — image */}
          <div className="relative min-h-[400px] lg:min-h-[500px] bg-gradient-to-br from-[#1a3d20] to-[#2d6b38]">
            <img
              src="/images/buggyteam2.jpg"
              alt="Buggy"
              className="absolute inset-0 w-full h-full object-cover"
              onError={e => { e.target.style.display = 'none'; }}
            />
            {/* gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-l from-transparent to-black/10" />
            {/* emoji fallback */}
<div className="absolute inset-0 flex items-center justify-center pointer-events-none">

  <img
    src="/images/buggyteam.jpg"
    alt="Buggy"
    className="w-full h-full object-cover"
  />

</div>          </div>
        </div>

        {/* ══ GALLERY ══ */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-14">
          {GALLERY.map((img, i) => (
            <motion.div
              key={i}
              whileHover={{ scale: 1.04 }}
              transition={{ type: 'spring', stiffness: 300, damping: 22 }}
              className={`
                rounded-[18px] overflow-hidden
                border-[2px] border-[#e7dbc7]
                relative aspect-[4/3]
                bg-gradient-to-br ${GALLERY_GRADIENTS[i]}
                flex items-center justify-center
                cursor-pointer
              `}
            >
              {/* fallback emoji always visible behind image */}
              <span className="absolute text-[48px] select-none">{img.fallback}</span>
              <img
                src={img.src}
                alt={`gallery-${i}`}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 hover:scale-[1.07]"
                onError={e => { e.target.style.display = 'none'; }}
              />
            </motion.div>
          ))}
        </div>

        {/* ══ PRICING TITLE ══ */}
        <div className="flex items-center justify-center gap-0 mb-8">
          <div className="
            bg-[#5b3a1c] text-white
            px-10 py-4 rounded-2xl
            text-[22px] font-extrabold
            shadow-[0_6px_0_#3a2510,0_8px_20px_rgba(91,58,28,.3)]
            relative
            before:content-['🌟'] before:absolute before:top-1/2 before:-translate-y-1/2 before:-left-10 before:text-[22px]
            after:content-['🌟'] after:absolute after:top-1/2 after:-translate-y-1/2 after:-right-10 after:text-[22px]
          ">
            {isJa ? '料金プラン' : 'Pricing Plans'}
          </div>
        </div>

        {/* ══ PRICE CARDS ══ */}
        <div className="grid md:grid-cols-3 gap-5 mb-6">
          {PRICES.map((item, i) => (
            <PriceCard key={i} item={item} isJa={isJa} />
          ))}
        </div>

        {/* small note */}
        <p className="text-center text-[13px] font-bold text-[#888] mb-12">
          {isJa
            ? '※ 料金は税込です。当日現地にてお支払いください。'
            : '* All prices include tax. Payment collected on the day at the venue.'}
        </p>

        {/* ══ PRIVATE PLAN ══ */}
        <motion.div
          whileHover={{ y: -4 }}
          transition={{ type: 'spring', stiffness: 280, damping: 22 }}
          className="
            grid lg:grid-cols-2 rounded-[24px] overflow-hidden mb-14
            border-[2.5px] border-[#d8c6a8]
            bg-[#fffdf8]
            shadow-[0_6px_0_#d8c6a8,0_10px_24px_rgba(30,40,20,.07)]
          "
        >
          <div className="p-10 flex items-center gap-6">
            <div className="text-[64px] leading-none flex-shrink-0">🎪</div>
            <div>
              <h3 className="font-extrabold text-[#173617] leading-tight mb-3"
                style={{ fontSize: 'clamp(30px,4vw,44px)' }}>
                {isJa ? '貸切プラン' : 'Private Plan'}
                <span className="block text-[14px] font-bold text-[#aaa] mt-1">
                  {isJa ? 'Private Plan' : '貸切プラン'}
                </span>
              </h3>
              <p className="text-[15px] font-bold text-[#555] leading-[1.85]">
                {isJa ? (
                  <>グループ・ご家族に最適な<br />貸切プランあり。<br />詳細はお電話にて。</>
                ) : (
                  <>Perfect for groups and families.<br />Private buggy plans available.<br />Please call for details.</>
                )}
              </p>
            </div>
          </div>

          <div className="relative min-h-[260px] bg-gradient-to-br from-[#1a3d20] to-[#3d8c4a] flex items-center justify-center">
            <img
              src="/images/buggyteam2.jpeg"
              alt="Private Plan"
              className="absolute inset-0 w-full h-full object-cover"
              onError={e => { e.target.style.display = 'none'; }}
            />
          </div>
        </motion.div>

        {/* ══ PHONE CTA ══ */}
        <div className="flex justify-center">
          <motion.a
            href="tel:0537269489"
            whileHover={{ y: -3 }}
            whileTap={{ y: 1 }}
            className="
              flex items-center gap-4
              bg-gradient-to-r from-[#173617] via-[#214d21] to-[#173617]
              text-white no-underline
              rounded-full
              px-12 py-6
              font-extrabold
              shadow-[0_8px_0_#0d2010,0_12px_32px_rgba(20,50,20,.3)]
              relative overflow-hidden
              transition-shadow duration-200
            "
            style={{ fontSize: 'clamp(18px,2.5vw,26px)' }}
          >
            {/* shimmer */}
            <span className="absolute inset-0 bg-gradient-to-br from-white/[.07] to-transparent pointer-events-none"/>
            <span>🌿</span>
            <span>{isJa ? '☎ 詳細・ご予約はお電話で' : '☎ Call For Details & Booking'}</span>
            <span>🌿</span>
          </motion.a>
        </div>

      </div>
    </section>
  );
}